/**
 * VIN field with live decode status. Renders the antd Form.Item for the VIN
 * plus the inline status adornment shown in the screenshots:
 *   - loading  -> "Extracting vehicle info" + spinner
 *   - success  -> green dot
 *   - notFound -> "Vehicle not found" message, manual entry stays enabled
 *   - error    -> retryable error message
 *
 * Lookups are triggered by the parent modal via the Form's `onValuesChange`
 * (so the antd value binding is never broken by a custom onChange). This
 * component only reflects the resulting `status` back to the user.
 *
 * @module vehicleModal/components/VinLookupSection
 */
import { CheckCircleFilled, LoadingOutlined } from "@ant-design/icons";
import { Form, Input } from "antd";
import React from "react";
import { vinRules } from "../utils/vehicleValidation";

/**
 * @param {Object} props
 * @param {import("../types").VinLookupStatus} props.status
 * @param {string|null} [props.error]
 */
export default function VinLookupSection({ status, error }) {
  return (
    <Form.Item
      name="vin"
      label="VIN"
      rules={vinRules()}
      validateStatus={status === "error" ? "error" : undefined}
      className="w-2/3"
    >
      <Input
        placeholder="Enter VIN"
        maxLength={17}
        allowClear
        suffix={<VinStatusAdornment status={status} error={error} />}
      />
    </Form.Item>
  );
}

/**
 * The right-aligned status indicator inside the VIN input.
 * @param {{ status: import("../types").VinLookupStatus, error?: string|null }} props
 */
function VinStatusAdornment({ status, error }) {
  if (status === "loading") {
    return (
      <span className="flex flex-row items-center gap-1 text-xs text-[#64748B]">
        Extracting vehicle info <LoadingOutlined spin />
      </span>
    );
  }
  if (status === "success") {
    return <CheckCircleFilled className="text-green-500" data-testid="vin-success" />;
  }
  if (status === "notFound") {
    return (
      <span className="text-xs text-[#d4380d]" data-testid="vin-not-found">
        Vehicle not found
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="text-xs text-[#d4380d]" title={error || ""} data-testid="vin-error">
        Lookup failed — retry
      </span>
    );
  }
  return <span />;
}
